import { signedFetch } from '../../super-picker/services/api';
import { PipelineCandidate, PipelineDraft, PipelineItem, PageLayout, PipelineSavedDraft, PipelineDraftRevision } from '../types';

const PREFIX = '/api/project-deck';

async function toJson<T>(res: Response, action: string): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string })?.error || `Failed to ${action}`);
  }
  return res.json() as Promise<T>;
}

/** Project Deck gate: TransformationAppUserAccess App='PROJECT-DECK', Role='ADMIN'. */
export async function fetchPipelineAccess(): Promise<boolean> {
  const res = await signedFetch(`${PREFIX}/access`);
  const data = await toJson<{ allowed: boolean }>(res, 'check access');
  return !!data.allowed;
}

type Candidates = { projects: PipelineCandidate[]; landSales: PipelineCandidate[] };

async function loadCandidates(): Promise<Candidates> {
  const res = await signedFetch(`${PREFIX}/candidates`);
  return toJson(res, 'fetch candidates');
}

async function loadSpecialPromoCandidates(): Promise<Candidates> {
  const res = await signedFetch(`${PREFIX}/candidates?mode=special-promo`);
  return toJson(res, 'fetch Special Promo candidates');
}

// The candidates scan is the module's heaviest call, so it is fetched once
// per session and shared; the app shell prefetches it as soon as access is
// confirmed so the Input tab renders instantly.
let candidatesCache: Promise<Candidates> | null = null;
let specialPromoCandidatesCache: Promise<Candidates> | null = null;

export function invalidateCandidatesCache(): void {
  candidatesCache = null;
}

export function prefetchCandidates(): void {
  if (!candidatesCache) {
    candidatesCache = loadCandidates();
    // Reset on failure so the Input tab retries (and surfaces the error).
    candidatesCache.catch(() => { candidatesCache = null; });
  }
}

export async function fetchCandidates(forceRefresh = false): Promise<Candidates> {
  if (forceRefresh || !candidatesCache) {
    candidatesCache = forceRefresh
      ? signedFetch(`${PREFIX}/candidates?refresh=1`).then((res) => toJson<Candidates>(res, 'fetch candidates'))
      : loadCandidates();
    candidatesCache.catch(() => { candidatesCache = null; });
  }
  return candidatesCache;
}

export async function fetchSpecialPromoCandidates(forceRefresh = false): Promise<Candidates> {
  if (forceRefresh || !specialPromoCandidatesCache) {
    specialPromoCandidatesCache = forceRefresh
      ? signedFetch(`${PREFIX}/candidates?mode=special-promo&refresh=1`).then((res) => toJson<Candidates>(res, 'fetch Special Promo candidates'))
      : loadSpecialPromoCandidates();
    specialPromoCandidatesCache.catch(() => { specialPromoCandidatesCache = null; });
  }
  return specialPromoCandidatesCache;
}

export async function fetchItems(): Promise<PipelineItem[]> {
  const res = await signedFetch(`${PREFIX}/items`);
  const data = await toJson<{ items: PipelineItem[] }>(res, 'fetch pipeline items');
  return data.items;
}

export async function generatePipeline(drafts: PipelineDraft[]): Promise<PipelineItem[]> {
  const res = await signedFetch(`${PREFIX}/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: drafts }),
  });
  const data = await toJson<{ items: PipelineItem[] }>(res, 'generate pipeline');
  invalidateCandidatesCache();
  return data.items;
}

export async function updateItem(id: number, patch: Partial<PipelineDraft>): Promise<PipelineItem> {
  const res = await signedFetch(`${PREFIX}/items/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const data = await toJson<{ item: PipelineItem }>(res, 'update pipeline item');
  return data.item;
}

export async function fetchLayouts(): Promise<Record<string, PageLayout | null>> {
  const res = await signedFetch(`${PREFIX}/layouts`);
  const data = await toJson<{ layouts: Record<string, PageLayout | null> }>(res, 'fetch layouts');
  return data.layouts || {};
}

export async function saveLayout(pageKey: string, layout: PageLayout): Promise<void> {
  const res = await signedFetch(`${PREFIX}/layouts/${pageKey}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ layout }),
  });
  await toJson(res, 'save layout');
}

export async function fetchDraft(): Promise<PipelineSavedDraft | null> {
  const res = await signedFetch(`${PREFIX}/draft`);
  const data = await toJson<{ draft: PipelineSavedDraft | null }>(res, 'fetch draft');
  return data.draft ?? null;
}

export async function uploadProjectDeckBackground(imageData: string, kind: 'cover' | 'deck'): Promise<string> {
  const res = await signedFetch(`${PREFIX}/draft/background`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageData, kind }),
  });
  const data = await toJson<{ url: string }>(res, 'upload background');
  return data.url;
}

export async function prepareProjectDeckBackgroundForExport(source: string): Promise<string> {
  if (!source || source.startsWith('data:') || !source.startsWith('https://firebasestorage.googleapis.com/')) {
    return source;
  }
  const res = await signedFetch(`${PREFIX}/draft/background?url=${encodeURIComponent(source)}`);
  if (!res.ok) throw new Error('Could not load uploaded background for PDF export');
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function publishResidentialShowcase(input: {
  imageDataUrl: string;
  imageWidth: number;
  imageHeight: number;
}): Promise<{
  id: number;
  title: string;
  imageUrl: string;
  isPublished: boolean;
  notification?: { sent: boolean; skipped: boolean };
}> {
  const res = await signedFetch(`${PREFIX}/publish/residential`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = await toJson<{ item: { id: number; title: string; imageUrl: string; isPublished: boolean; notification?: { sent: boolean; skipped: boolean } }; notification?: { sent: boolean; skipped: boolean } }>(res, 'publish Residential showcase');
  if (data.notification && !data.item.notification) data.item.notification = data.notification;
  return data.item;
}

export async function saveDraft(draft: PipelineSavedDraft): Promise<PipelineSavedDraft> {
  const layouts = Object.fromEntries(
    Object.entries(draft.layouts).map(([pageKey, layout]) => [pageKey, { ...layout }]),
  ) as PipelineSavedDraft['layouts'];
  const prepared: PipelineSavedDraft = { ...draft, layouts };
  const sharedBackgrounds: Array<{ key: string; kind: 'cover' | 'deck' }> = [
    { key: '__main-background', kind: 'cover' },
    { key: '__downstream-background', kind: 'deck' },
  ];
  for (const { key, kind } of sharedBackgrounds) {
    const entry = prepared.layouts[key]?.__background;
    const image = entry?.backgroundImage;
    if (typeof image === 'string' && image.startsWith('data:')) {
      const url = await uploadProjectDeckBackground(image, kind);
      prepared.layouts[key] = {
        ...prepared.layouts[key],
        __background: { ...entry, backgroundImage: url },
      };
    }
  }
  const res = await signedFetch(`${PREFIX}/draft`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ draft: prepared }),
  });
  await toJson(res, 'save draft');
  return prepared;
}

export async function fetchDraftRevisions(): Promise<PipelineDraftRevision[]> {
  const res = await signedFetch(`${PREFIX}/draft/revisions`);
  const data = await toJson<{ revisions: PipelineDraftRevision[] }>(res, 'fetch draft revisions');
  return data.revisions;
}

export async function restoreDraftRevision(id: number): Promise<PipelineSavedDraft> {
  const res = await signedFetch(`${PREFIX}/draft/revisions/${id}/restore`, { method: 'POST' });
  const data = await toJson<{ draft: PipelineSavedDraft }>(res, 'restore draft revision');
  return data.draft;
}
